'use client'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import type { Settings } from 'react-slick'
import '@/styles/vendors/slickCarousel'
import { Content, isFilled } from '@prismicio/client'
import { PrismicRichText } from '@prismicio/react'
import { sliderSettingsWithoutDot } from '@/utils/sliderSettings'
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next'

// Lazy load Slider to reduce initial bundle size
const Slider = dynamic<Settings>(() => import('react-slick'), {
  ssr: false,
  loading: () => <div className="h-64 animate-pulse rounded-md bg-gray-100" />,
})

type TestimonialItem = Content.TestimonialsSlice['items'][number]

const TestimonialCard = ({
  item,
  className,
  bodyClassName = '',
}: {
  item: TestimonialItem
  className: string
  bodyClassName?: string
}) => {
  const card = (
    <div className={`origin-center transition-transform duration-300 ease-out group-hover:scale-[1.01] ${className}`}>
      <div>
        <PrismicNextImage field={item.icon} fallbackAlt="" />
      </div>

      <div className={`flex flex-col justify-between ${bodyClassName}`}>
        <div className="[&>h3]:min-h-[220px]">
          <PrismicRichText
            field={item.quote}
            components={{
              heading4: ({ children }) => (
                <h3 className="text-xl font-normal tracking-[-0.45px] transition-colors duration-300 xl:text-3xl group-hover:text-primary-green-85">
                  {children}
                </h3>
              ),
            }}
          />
        </div>

        <div>
          <span className="block text-base font-semibold" role="none">
            {item.name}
          </span>
          <span className="block text-xs text-primary-ink-60" role="none">
            {item.designation}
          </span>
        </div>
      </div>

      <div className="btn-interaction flex gap-2">
        <span className="inline-block">{item.btn_text}</span>
        <Image src="/images/angle-right-green.svg" width={16} height={13} alt="" aria-hidden="true" />
      </div>
    </div>
  )

  if (!isFilled.link(item.btn_link)) {
    return card
  }

  return (
    <PrismicNextLink
      field={item.btn_link}
      className="group block h-full rounded-md outline-none focus-visible:ring-2 focus-visible:ring-primary-green-85 focus-visible:ring-offset-2">
      {card}
    </PrismicNextLink>
  )
}

const TestimonialsComponent = ({ data, type }: { data: Content.TestimonialsSlice; type?: string }) => {
  return (
    <div
      className={`relative z-[1] py-20 lg:py-[140px] ${type === 'featureTestimonial' ? 'lg:pb-[140px] lg:pt-0' : ''} ${type ?? ''}`}>
      <div className="container">
        <PrismicRichText field={data.primary.title} />

        {/* testimonial desktop version card */}
        <div className="mt-16 hidden lg:grid lg:grid-cols-2 lg:gap-8 lg:pl-[152px]">
          {data.items.map((item, index) => (
            <TestimonialCard
              key={`i-${index}`}
              item={item}
              className="rounded-md border border-primary-ink-6 px-6 py-8 shadow-light backdrop-blur-xl [&>h3]:min-h-[220px]"
            />
          ))}
        </div>

        {/* testimonial mobile card */}
        <div className="mt-16 block lg:hidden">
          <Slider
            {...sliderSettingsWithoutDot}
            className="[&.slick-slider]:-mx-4 [&.slick-slider]:my-0 [&_.slick-slide]:px-3">
            {data.items.map((item, index) => (
              <TestimonialCard
                key={`i-${index}`}
                item={item}
                className="rounded-md border border-secondary-gray-150 px-6 py-8 shadow-light backdrop-blur-sm sm:backdrop-blur-xl [&>h3]:min-h-[220px]"
                bodyClassName="mt-4"
              />
            ))}
          </Slider>
        </div>
      </div>
    </div>
  )
}

export default TestimonialsComponent
